import React, { useState } from 'react';
import { FadeIn, SectionTitle, Btn, Card } from '../components/UI';
import { submitContact } from '../services/api';

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    if (!form.name || !form.email || !form.message) {
      setErrorMsg('Please fill in all required fields.');
      setStatus('error');
      return;
    }

    setStatus('sending');
    setErrorMsg('');

    try {
      await submitContact(form);
      setStatus('success');
    } catch (err) {
      setErrorMsg(err.message || 'Something went wrong. Please try again.');
      setStatus('error');
    }
  };

  const inputStyle = {
    width: '100%', boxSizing: 'border-box',
    background: 'rgba(255,255,255,0.035)',
    border: '1px solid var(--card-border)',
    borderRadius: 8, padding: '13px 16px',
    fontFamily: 'var(--font-body)', fontSize: '0.92rem', fontWeight: 300,
    color: '#fff', outline: 'none', transition: 'border-color 0.3s',
  };

  return (
    <div style={{ paddingTop: 100, background: 'var(--dark)' }}>
      <section style={{ padding: '60px 40px 100px', maxWidth: 900, margin: '0 auto' }}>
        <FadeIn>
          <SectionTitle subtitle="Get In Touch" title="Let's talk about your project" />
        </FadeIn>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: 48,
        }}>
          <FadeIn delay={0.1}>
            {status === 'success' ? (
              <div style={{
                background: 'rgba(41,182,246,0.06)',
                border: '1px solid rgba(41,182,246,0.2)',
                borderRadius: 12, padding: 40, textAlign: 'center',
              }}>
                <div style={{ fontSize: '2.5rem', marginBottom: 14 }}>✓</div>
                <h3 style={{
                  fontFamily: 'var(--font-body)', fontWeight: 600,
                  color: 'var(--cyan)', fontSize: '1.2rem', marginBottom: 8,
                }}>Message Sent</h3>
                <p style={{
                  fontFamily: 'var(--font-body)', fontWeight: 300,
                  color: 'var(--text-secondary)', lineHeight: 1.6,
                }}>
                  Thanks {form.name}! We'll be in touch shortly.
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {[
                  { name: 'name', placeholder: 'Your Name *', type: 'text' },
                  { name: 'email', placeholder: 'Email Address *', type: 'email' },
                  { name: 'company', placeholder: 'Company Name', type: 'text' },
                ].map(f => (
                  <input
                    key={f.name}
                    name={f.name}
                    type={f.type}
                    placeholder={f.placeholder}
                    value={form[f.name]}
                    onChange={handleChange}
                    style={inputStyle}
                    onFocus={e => e.target.style.borderColor = 'var(--cyan)'}
                    onBlur={e => e.target.style.borderColor = 'var(--card-border)'}
                  />
                ))}
                <textarea
                  name="message"
                  placeholder="Tell us about your project *"
                  value={form.message}
                  onChange={handleChange}
                  style={{ ...inputStyle, minHeight: 130, resize: 'vertical' }}
                  onFocus={e => e.target.style.borderColor = 'var(--cyan)'}
                  onBlur={e => e.target.style.borderColor = 'var(--card-border)'}
                />

                {status === 'error' && (
                  <p style={{
                    fontFamily: 'var(--font-body)', fontSize: '0.85rem',
                    color: '#ef5350', marginBottom: -4,
                  }}>
                    {errorMsg}
                  </p>
                )}

                <Btn large onClick={handleSubmit}>
                  {status === 'sending' ? 'Sending...' : 'Send Message'}
                </Btn>
              </div>
            )}
          </FadeIn>

          <FadeIn delay={0.2}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              {[
                { label: 'Email', value: 'hello@knowitsolutions.co.uk', icon: '✉️' },
                { label: 'Phone', value: '+44 (0) 123 456 7890', icon: '📞' },
                { label: 'Location', value: 'United Kingdom', icon: '📍' },
              ].map((c, i) => (
                <div key={i} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 8,
                    background: 'rgba(41,182,246,0.07)',
                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                    fontSize: '1.1rem', flexShrink: 0,
                  }}>{c.icon}</div>
                  <div>
                    <p style={{
                      fontFamily: 'var(--font-body)', fontSize: '0.75rem',
                      fontWeight: 400, color: 'var(--text-muted)',
                      marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.08em',
                    }}>{c.label}</p>
                    <p style={{
                      fontFamily: 'var(--font-body)', fontSize: '0.95rem',
                      fontWeight: 400, color: 'var(--text-primary)',
                    }}>{c.value}</p>
                  </div>
                </div>
              ))}

              <Card style={{ marginTop: 8 }}>
                <h4 style={{
                  fontFamily: 'var(--font-body)', fontWeight: 600,
                  color: 'var(--text-primary)', marginBottom: 6, fontSize: '0.95rem',
                }}>Prefer a chat?</h4>
                <p style={{
                  fontFamily: 'var(--font-body)', fontSize: '0.88rem',
                  fontWeight: 300, color: 'var(--text-secondary)', lineHeight: 1.6,
                }}>
                  Book a free 30-minute consultation to discuss your requirements.
                  No obligation, no hard sell — just honest advice.
                </p>
              </Card>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
